/**
 * 旗 oneAccount が on のときのログイン（2026-10-07 console/one-account・設計 §2）。旗が off ならこの画面は出ない（App.tsx が今の LoginPage を出す）。
 *
 *   /login            「ログインへ進む」→ account（<accountUrl>/account/console?challenge=…）。人の確かめは account だけで行う
 *                     （メールのリンク / コード・Google / X / Discord・移した作り手のパスワード等。どれを出すかは account が決める）
 *   /signin/callback  account からの戻り（#code=…）。読んだらすぐ URL から消し、POST /v1/account/console/session で kas_（と作り手なら kds_）に換える
 *
 * 移行の間の例外（設計 §2）: 今のログイン用パスキー（RP ID = ダッシュボードのホスト）は account では使えない。
 *   サーバーが passkeyLogin を返す間は、ここに「前にこのページでパスキーを保存しましたか？」とボタンを残す。入った後は 1 回だけ
 *   「アカウントのページで登録し直して」を出す（components/ModeSwitch.tsx の OldPasskeyNotice・印は sessionStorage の OLD_PASSKEY_KEY）。
 */
import { startTransition, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../api';
import { createConsoleSession, signInPendingStorage, startAccountSignIn } from '../accountApi';
import { afterLoginPath } from '../authForms';
import { PasskeyIcon, useLoginOptions } from '../components/OAuthButtons';
import { BootScreen } from '../components/ui';
import { errorText, useT } from '../i18n';
import { isLaunchHidden } from '../launchHidden';
import { passkeyLoginError, showPasskeyLogin } from '../loginOptions';
import { passkeySignIn } from '../loginPasskeyClient';
import { defaultMode, modeHome, OLD_PASSKEY_KEY, parseSignInCallback, readSavedMode, SIGNIN_CALLBACK_PATH, takePendingSignIn } from '../oneAccount';
import { passkeysSupported } from '../passkey';
import { useSession } from '../session';
import { AuthLayout, ErrorLine, FormTop } from './AuthPage';

const local = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export function AccountSignInPage() {
  const { t } = useT();
  const { oneAccount, signIn } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const from = (location.state as { from?: unknown } | null)?.from;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options = useLoginOptions();
  const oldPasskey = showPasskeyLogin(options, passkeysSupported());
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  const go = async () => {
    if (!oneAccount.accountUrl) return;
    setError(null);
    setBusy(true);
    // 戻り先は afterLoginPath で確かめてから控えに入れる（外へは行かない）。/signup から来た人（サーバーの invite_code_required の signupUrl）は
    // 入った後に「招待コードを入れる」画面へ（App.tsx が /signup をそこへ移す）
    const returnTo = from === '/signup' ? '/signup' : afterLoginPath(from);
    const ok = await startAccountSignIn(oneAccount.accountUrl, returnTo).catch(() => false);
    if (!ok) {
      setError(t('oneAccount.signIn.storageBlocked'));
      setBusy(false);
    }
  };

  const withOldPasskey = async () => {
    setError(null);
    setPasskeyBusy(true);
    try {
      const r = await passkeySignIn();
      try {
        sessionStorage.setItem(OLD_PASSKEY_KEY, '1');
      } catch {
        /* 案内が出ないだけ（ログインはできている） */
      }
      startTransition(() => {
        navigate(afterLoginPath(from));
        signIn(r.token, r.developer);
      });
    } catch (err) {
      // 文言は今のログインの画面のパスキーのボタンと同じ（components/OAuthButtons.tsx）
      const kind = passkeyLoginError(err);
      if (kind === 'failed') setError(t(isLaunchHidden('payouts') ? 'auth.passkeyFailedLaunch' : 'auth.passkeyFailed'));
      else if (kind === 'unavailable') setError(t('auth.passkeyUnavailable'));
      else if (kind === 'other') setError(errorText(err as ApiError));
      setPasskeyBusy(false);
    }
  };

  return (
    <AuthLayout>
      <div className="auth-form">
        <FormTop title={t('oneAccount.signIn.title')} lead={t('oneAccount.signIn.lead')} />
        <ErrorLine error={error} />
        <button type="button" className="btn btn-primary btn-block auth-submit" disabled={busy || passkeyBusy} onClick={() => void go()}>
          {busy ? t('oneAccount.signIn.busy') : t('oneAccount.signIn.button')}
        </button>
        {oldPasskey && (
          <div className="oauth-block">
            <div className="auth-divider" role="separator">
              <span>{t('oauth.or')}</span>
            </div>
            <p className="muted">
              {t('oneAccount.signIn.oldPasskeyTitle')} {t('oneAccount.signIn.oldPasskeyLead')}
            </p>
            <button type="button" className="btn btn-block oauth-btn oauth-passkey" disabled={busy || passkeyBusy} onClick={() => void withOldPasskey()}>
              <PasskeyIcon />
              <span>{passkeyBusy ? t('auth.busy') : t('auth.passkeyLogin')}</span>
            </button>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}

/**
 * 読んだ結果をモジュールに持つ。WHY: StrictMode（開発）は state の初期化と effect を 2 回走らせる。控えもコードも一回限りなので、
 * 2 回目で「控えが無い」「コードが使用済み」にならないよう 1 回目の結果を使い回す（pages/OAuthCallback.tsx と同じ）
 */
let parsed: { key: string; code: string | null; pending: ReturnType<typeof takePendingSignIn> } | null = null;
let started: Promise<string> | null = null;

function readOnce() {
  const key = window.location.hash;
  if (!parsed || (key && parsed.key !== key)) {
    parsed = { key, code: parseSignInCallback(key).code, pending: takePendingSignIn(signInPendingStorage()) };
    started = null;
  }
  return parsed;
}

type View = { kind: 'working' } | { kind: 'failed'; reason: 'expired' | 'otherTab' | 'error'; message?: string };

export function SignInCallbackPage() {
  const { t } = useT();
  const { signInConsole } = useSession();
  const navigate = useNavigate();
  const [{ code, pending }] = useState(readOnce);
  const [view, setView] = useState<View>({ kind: 'working' });

  useEffect(() => {
    // コードを URL（履歴・画面共有）に残さない
    if (window.location.hash) window.history.replaceState(window.history.state, '', SIGNIN_CALLBACK_PATH);
    if (!code) return setView({ kind: 'failed', reason: 'expired' });
    // 控えが無い = このタブで始めていない（別のタブ・再読み込み・他人に踏まされたリンク）→ 交換しない
    if (!pending) return setView({ kind: 'failed', reason: 'otherTab' });
    started ??= createConsoleSession(code, pending.verifier).then(async (s) => {
      await signInConsole(s);
      // 戻り先が決まっていればそこへ。無ければ最後に選んだ方 → 作り手の中身の有無で（設計 §3）
      if (pending.returnTo !== '/') return pending.returnTo;
      return modeHome(defaultMode(s.creator, readSavedMode(local())), s.creator);
    });
    let live = true;
    started.then(
      (to) => live && navigate(to, { replace: true }),
      (err: unknown) => {
        if (!live) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 400)) setView({ kind: 'failed', reason: 'expired' });
        else setView({ kind: 'failed', reason: 'error', message: err instanceof ApiError ? errorText(err) : undefined });
      }
    );
    return () => {
      live = false;
    };
  }, [code, pending, navigate, signInConsole]);

  if (view.kind === 'working') {
    return <BootScreen label={t('oneAccount.callback.working')} />;
  }
  return (
    <AuthLayout>
      <div className="auth-form">
        <FormTop title={t('oneAccount.callback.failedTitle')} lead={view.message ?? t(view.reason === 'otherTab' ? 'oneAccount.callback.otherTab' : 'oneAccount.callback.expired')} />
        <Link to="/login" replace className="btn btn-primary btn-block auth-submit">
          {t('oneAccount.callback.again')}
        </Link>
      </div>
    </AuthLayout>
  );
}
